/* ========================================================================
   PLP用ウィッシュリスト初期化スクリプト
   Wishlist Hero SDK を利用し、ページ内のブックマークボタン状態を同期する。
   ======================================================================== */
(() => {
  const wishlistButtonData = window.AuremePlpWishlistButtonData;
  if (
    !wishlistButtonData ||
    typeof wishlistButtonData.applyButtonAddedState !== 'function' ||
    typeof wishlistButtonData.findButtons !== 'function' ||
    typeof wishlistButtonData.readButtonDataset !== 'function'
  ) {
    console.error('[aureme-plp] wishlist button data helper is not loaded.');
    return;
  }

  const wishlistSdk = window.AuremeWishlistSdk;
  if (!wishlistSdk || typeof wishlistSdk.buildProductUrl !== 'function') {
    console.error('[aureme-plp] Wishlist Hero SDK helper is not loaded.');
    return;
  }

  const FAVORITES_DEFAULT_ERROR_MSG =
    'エラーが発生しました。\nページを再読み込みしてお試しください。';
  const FAVORITES_HASH_ERROR_MSG =
    'お気に入り操作に必要な情報を取得できませんでした。\nページを再読み込みしてから再度お試しください。';
  const PLP_SERVER_FALLBACK_WARNING =
    '[aureme-plp] Wishlist Hero SDK のサーバー取得に失敗したためキャッシュを参照します。';
  const NAVIGATION_ERROR_FLAG = 'auremeNavigationError';
  const PLP_WISHLIST_BUTTON_SELECTOR = wishlistButtonData.buttonSelector;
  const PLP_WISHLIST_VARIANT_CHANGE_EVENT = wishlistButtonData.variantChangeEventName;
  let currentWishlistData = null;
  let isDelegatedEventsBound = false;
  let navigating = false;
  let navigationVersion = 0;
  let wishlistMutationContext = null;
  let wishlistVariantIds = null;
  let wishlistVariantIdsPromise = null;
  const syncedVariantIdByButton = new WeakMap();
  const {
    buildProductUrl,
    buildWishlistVariantIdSet,
    buildWishlistProductTitle,
    createWishlistMutationContext,
    getWishlistErrorToastMessage,
    getWishlistSuccessToastMessage,
    normalizeId,
    normalizeSdkId,
    normalizeText,
    normalizeWishlistOptionText,
    showErrorToast: showWishlistErrorToast,
    showToast: showWishlistToast,
    waitForWishlistSdk,
  } = wishlistSdk;

  /*
   * pagehide / beforeunload 後に完了した SDK 通信は、次画面の通常エラーとして扱わない。
   * 世代番号を残し、BFCache 復帰後に遅れて reject した旧リクエストだけを黙殺する。
   */
  function markNavigating() {
    if (!navigating) {
      navigationVersion += 1;
    }
    navigating = true;
  }

  window.addEventListener('pagehide', markNavigating);
  window.addEventListener('beforeunload', markNavigating);
  window.addEventListener('pageshow', () => {
    /* 復帰後の新規リクエストは通常扱いしつつ、旧リクエストは世代差分で黙殺する */
    navigating = false;
  });

  function shouldIgnoreError(error) {
    return Boolean(navigating || error?.[NAVIGATION_ERROR_FLAG]);
  }

  function attachNavigationFlag(error, requestNavigationVersion) {
    if (error && (requestNavigationVersion !== navigationVersion || navigating)) {
      error[NAVIGATION_ERROR_FLAG] = true;
    }
  }

  function showErrorToast(message = FAVORITES_DEFAULT_ERROR_MSG) {
    showWishlistErrorToast(message);
  }

  function getMutationErrorMessage(error, isCurrentlyAdded) {
    if (error?.wishlistHashMissing) return FAVORITES_HASH_ERROR_MSG;

    return getWishlistErrorToastMessage(isCurrentlyAdded);
  }

  function getWishlistMutationContext(sdk) {
    if (!wishlistMutationContext) {
      wishlistMutationContext = createWishlistMutationContext(sdk, {
        serverFallbackWarning: PLP_SERVER_FALLBACK_WARNING,
      });
    }

    return wishlistMutationContext;
  }

  async function refreshWishlistVariantIds(sdk, { preferServer = true } = {}) {
    const wishlistItems = await getWishlistMutationContext(sdk).fetchItems({ preferServer });
    wishlistVariantIds = buildWishlistVariantIdSet(wishlistItems);
    return wishlistVariantIds;
  }

  /*
   * 初期同期の wishlist 一覧取得は複数ボタンから同時に呼ばれるため、取得中 promise を共有する。
   * 成功後は variant ID Set を保持し、mutate 後は toggleWishlistItem 側で最新 Set に差し替える。
   */
  async function getWishlistVariantIds() {
    if (wishlistVariantIds) return wishlistVariantIds;
    if (wishlistVariantIdsPromise) return wishlistVariantIdsPromise;

    wishlistVariantIdsPromise = (async () => {
      const sdk = await waitForWishlistSdk();
      return refreshWishlistVariantIds(sdk, { preferServer: true });
    })();

    try {
      return await wishlistVariantIdsPromise;
    } finally {
      wishlistVariantIdsPromise = null;
    }
  }

  function normalizeProductImage(imageUrl) {
    const normalizedImageUrl = normalizeText(imageUrl);
    if (!normalizedImageUrl) return '';

    return normalizedImageUrl.replace(/^https?:/i, '');
  }

  function normalizeProductPrice(productPriceCents) {
    const cents = Number(productPriceCents);
    if (!Number.isFinite(cents)) return 0;

    /* Wishlist Hero の ProductPrice はテーマ内価格と同じく major unit で渡す */
    return Math.max(0, Math.round(cents) / 100);
  }

  function createWishlistProductSnapshot(button) {
    const buttonDataset = wishlistButtonData.readButtonDataset(button);
    const productId = normalizeId(buttonDataset.productId);
    const productVariantId = normalizeId(buttonDataset.productVariantId);
    const productHandle = normalizeText(buttonDataset.productHandle);

    return {
      productHandle,
      productId,
      productImage: normalizeProductImage(buttonDataset.productImage),
      productLink:
        normalizeText(buttonDataset.productUrl) ||
        buildProductUrl(productHandle, { productVariantId }),
      productOptionText: normalizeWishlistOptionText(buttonDataset.productOptionText),
      productPriceCents: buttonDataset.productPriceCents,
      productTitle: normalizeText(buttonDataset.productTitle),
      productVariantId,
    };
  }

  function buildWishlistProductInfo(productSnapshot, wishlistHashValue) {
    const {
      productId,
      productImage,
      productLink,
      productOptionText,
      productPriceCents,
      productTitle,
      productVariantId,
    } = productSnapshot;

    if (!productId || !productVariantId || !productTitle || !productLink || !productImage) {
      throw new Error('Wishlist Hero SDK product info is incomplete.');
    }

    const productInfo = {
      ProductId: normalizeSdkId(productId),
      ProductImage: productImage,
      ProductLink: productLink,
      ProductPrice: normalizeProductPrice(productPriceCents),
      ProductTitle: buildWishlistProductTitle(productTitle, productOptionText),
      ProductVariantId: normalizeSdkId(productVariantId),
      WishlistHash: wishlistHashValue,
    };

    return productInfo;
  }

  async function toggleWishlistItem(productSnapshot, isCurrentlyAdded) {
    const sdk = await waitForWishlistSdk();
    const productVariantId = productSnapshot.productVariantId;
    const mutationContext = getWishlistMutationContext(sdk);
    const wishlistHashValue = await mutationContext.getRequiredHash({
      createIfMissing: !isCurrentlyAdded,
    });
    const mutationResult = await mutationContext.mutateItem({
      isCurrentlyAdded,
      productInfo: isCurrentlyAdded
        ? null
        : buildWishlistProductInfo(productSnapshot, wishlistHashValue),
      productVariantId,
      wishlistHash: wishlistHashValue,
    });

    wishlistVariantIds = buildWishlistVariantIdSet(mutationResult.wishlistItems);
    return mutationResult.isAdded;
  }

  /*
    同じ variant が複数カードや色切替後のボタンに現れるため、
    mutate 結果はページ内の一致ボタンへまとめて反映する。
  */
  function updateMatchingButtons(productVariantId, isAdded) {
    const normalizedProductVariantId = normalizeId(productVariantId);
    if (!normalizedProductVariantId) return;

    for (const button of wishlistButtonData.findButtons(document)) {
      if (normalizeId(button.dataset.productVariantId) !== normalizedProductVariantId) continue;

      wishlistButtonData.applyButtonAddedState(button, isAdded);
      syncedVariantIdByButton.set(button, normalizedProductVariantId);
    }
  }

  async function syncState(button, { force = false } = {}) {
    const productVariantId = normalizeId(button.dataset.productVariantId);
    if (!productVariantId) return;

    /* 同じボタン・同じ variant の再同期を省き、swatch 切替時だけ force で再判定する */
    if (!force && syncedVariantIdByButton.get(button) === productVariantId) return;

    try {
      const variantIds = await getWishlistVariantIds();
      wishlistButtonData.applyButtonAddedState(button, variantIds.has(productVariantId));
      syncedVariantIdByButton.set(button, productVariantId);
    } catch (error) {
      if (!shouldIgnoreError(error)) {
        console.error('PLP お気に入り状態取得エラー:', error);
      }
    }
  }

  /* トグル処理 */
  async function handleToggle(button, wishlistData) {
    if (!wishlistData.customerId) {
      openLoginModal(button);
      return;
    }

    const productSnapshot = createWishlistProductSnapshot(button);
    const productVariantId = productSnapshot.productVariantId;
    if (!productVariantId) {
      showErrorToast();
      return;
    }

    const requestNavigationVersion = navigationVersion;
    button.disabled = true;
    const isCurrentlyAdded = button.dataset.added === 'true';

    try {
      const nextIsAdded = await toggleWishlistItem(productSnapshot, isCurrentlyAdded);
      updateMatchingButtons(productVariantId, nextIsAdded);
      showWishlistToast(getWishlistSuccessToastMessage(nextIsAdded), 'success');
    } catch (error) {
      attachNavigationFlag(error, requestNavigationVersion);
      if (!shouldIgnoreError(error)) {
        console.error('PLP お気に入り更新エラー:', error);
        showErrorToast(getMutationErrorMessage(error, isCurrentlyAdded));
      }
    } finally {
      button.disabled = false;
    }
  }

  /* ログインモーダル */
  function openLoginModal(opener = null) {
    if (window.AuremeWishlistLoginModal?.open?.(opener)) {
      return;
    }

    console.error('[aureme-plp] wishlist login modal helper is not loaded.');
  }

  /* イベント委譲 */
  function bindDelegatedEvents() {
    if (isDelegatedEventsBound) return;

    isDelegatedEventsBound = true;
    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest(PLP_WISHLIST_BUTTON_SELECTOR);
      if (!button) return;
      if (!currentWishlistData) return;

      event.preventDefault();
      event.stopPropagation();
      handleToggle(button, currentWishlistData);
    });
  }

  function syncButtons() {
    if (!currentWishlistData?.customerId) return;

    for (const button of wishlistButtonData.findButtons(document)) {
      syncState(button);
    }
  }

  /* 初期化 */
  function init() {
    const dataEl = document.getElementById('AuremePlpWishlistData');
    if (!dataEl) return;

    try {
      currentWishlistData = JSON.parse(dataEl.textContent);
    } catch {
      currentWishlistData = null;
      return;
    }

    bindDelegatedEvents();
    syncButtons();
  }

  /* DOMContentLoaded後、またはすでにロード済みなら即時実行 */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener(PLP_WISHLIST_VARIANT_CHANGE_EVENT, (event) => {
    const card = event.detail?.card;
    if (!card) return;
    if (!currentWishlistData?.customerId) return;

    for (const button of wishlistButtonData.findButtons(card)) {
      syncState(button, { force: true });
    }
  });

  /* AJAX遷移後の再初期化対応 */
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        /* collection Ajax 差し替え後に追加されたカードだけ拾い直し、委譲イベントは再登録しない */
        if (
          node.matches?.(PLP_WISHLIST_BUTTON_SELECTOR) ||
          node.querySelector?.(PLP_WISHLIST_BUTTON_SELECTOR)
        ) {
          init();
          return;
        }
      }
    }
  });

  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    observer.observe(gridContainer, { childList: true, subtree: true });
  }
})();
